import { FormGroup, InputGroup, TextArea } from '@clarion-blueprint-shim';
import { useState } from 'react';
import { useUserState } from '../auth/UserState';
import { ClarionButton } from '../clarion/ClarionButton';
import { ClarionDialog, ClarionDialogFooter } from '../clarion/ClarionDialog';
import { Assignment, TimeEntry } from './assignment';
import { addManualTimeEntry, editTimeEntry } from './assignmentService';

interface TimeEntryFormProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: Assignment;
  existingEntry?: TimeEntry;
  onEntryAdded: (updatedAssignment: Assignment) => void;
}

// Helper function to format date for datetime-local input
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export default function TimeEntryForm({
  isOpen,
  onClose,
  assignment,
  existingEntry,
  onEntryAdded,
}: TimeEntryFormProps) {
  const { user } = useUserState();
  const [startDateTime, setStartDateTime] = useState<string>(
    existingEntry?.startTime
      ? formatDateForInput(new Date(existingEntry.startTime))
      : formatDateForInput(new Date()),
  );
  const [endDateTime, setEndDateTime] = useState<string>(
    existingEntry?.endTime
      ? formatDateForInput(new Date(existingEntry.endTime))
      : formatDateForInput(new Date()),
  );
  const [notes, setNotes] = useState(existingEntry?.notes || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = Boolean(existingEntry);

  const handleSubmit = async () => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const startTime = new Date(startDateTime).getTime();
      const endTime = new Date(endDateTime).getTime();

      // Validate dates
      if (isNaN(startTime) || isNaN(endTime)) {
        setError('Please enter valid date and time values');
        return;
      }

      if (startTime >= endTime) {
        setError('End time must be after start time');
        return;
      }

      let updatedAssignment: Assignment;

      if (isEditMode && existingEntry) {
        // Update existing entry
        updatedAssignment = await editTimeEntry(
          assignment.id,
          assignment.workspaceId,
          existingEntry.id,
          user.uid,
          user.displayName || user.email || 'Unknown User',
          {
            startTime,
            endTime,
            notes,
            isManualEntry: true,
          },
        );
      } else {
        // Add new time entry
        updatedAssignment = await addManualTimeEntry(
          assignment.id,
          assignment.workspaceId,
          user.uid,
          user.displayName || user.email || 'Unknown User',
          startTime,
          endTime,
          notes,
        );
      }

      onEntryAdded(updatedAssignment);
      onClose();
    } catch (error) {
      console.error('Error saving time entry:', error);
      setError('Failed to save time entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ClarionDialog
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Time Entry' : 'Add Time Entry'}
    >
      {error && <div className="mb-4 p-2 bg-red-100 text-red-800 rounded-sm">{error}</div>}

      <FormGroup label="Start Date/Time" labelFor="start-date" labelInfo="(required)">
        <InputGroup
          id="start-date"
          type="datetime-local"
          value={startDateTime}
          onChange={setStartDateTime}
          fill
        />
      </FormGroup>

      <FormGroup label="End Date/Time" labelFor="end-date" labelInfo="(required)">
        <InputGroup
          id="end-date"
          type="datetime-local"
          value={endDateTime}
          onChange={setEndDateTime}
          fill
        />
      </FormGroup>

      <FormGroup label="Notes" labelFor="notes-input">
        <TextArea
          id="notes-input"
          value={notes}
          onChange={setNotes}
          placeholder="Add notes about this time entry"
          fill
          autoResize
          style={{ minHeight: 80 }}
        />
      </FormGroup>

      <ClarionDialogFooter>
        <ClarionButton onClick={onClose} isDisabled={isSubmitting}>
          Cancel
        </ClarionButton>
        <ClarionButton
          intent="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          isDisabled={isSubmitting}
        >
          {isEditMode ? 'Update' : 'Add'} Time Entry
        </ClarionButton>
      </ClarionDialogFooter>
    </ClarionDialog>
  );
}
